import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { ChevronDown } from '@emotion-icons/boxicons-solid/ChevronDown'
import { ChevronUp } from '@emotion-icons/boxicons-solid/ChevronUp'
import { Colors, Shapes } from 'src/components/theme'
import { ColorPallete, alphaColorPallete } from 'src/components/button/palettes'
import { CircleIcon } from 'src/components/icons/circle-icon'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { TypographyPropsVariantOverrides } from '@mui/material/Typography/Typography'

const AlphamapInfoIcon = (props: { fill: string }) => {
  const { fill } = props
  return (
    <svg viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.01758 18.9844C5.7832 18.9844 2.83008 17.2969 1.21289 14.4844C-0.404297 11.707 -0.404297 8.29688 1.21289 5.48438C2.83008 2.70703 5.7832 0.984375 9.01758 0.984375C12.2168 0.984375 15.1699 2.70703 16.7871 5.48438C18.4043 8.29688 18.4043 11.707 16.7871 14.4844C15.1699 17.2969 12.2168 18.9844 9.01758 18.9844ZM7.61133 12.7969C7.11914 12.7969 6.76758 13.1836 6.76758 13.6406C6.76758 14.1328 7.11914 14.4844 7.61133 14.4844H10.4238C10.8809 14.4844 11.2676 14.1328 11.2676 13.6406C11.2676 13.1836 10.8809 12.7969 10.4238 12.7969H10.1426V9.70312C10.1426 9.24609 9.75586 8.85938 9.29883 8.85938H7.61133C7.11914 8.85938 6.76758 9.24609 6.76758 9.70312C6.76758 10.1953 7.11914 10.5469 7.61133 10.5469H8.45508V12.7969H7.61133ZM9.01758 5.48438C8.38477 5.48438 7.89258 6.01172 7.89258 6.60938C7.89258 7.24219 8.38477 7.73438 9.01758 7.73438C9.61523 7.73438 10.1426 7.24219 10.1426 6.60938C10.1426 6.01172 9.61523 5.48438 9.01758 5.48438Z"
        fill={fill}
      />
    </svg>
  )
}

const Wrapper = styled.div<{ color: keyof ColorPallete; cursor?: 'pointer' | 'default' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Shapes.spacing[4]}px;
  border-radius: ${Shapes.borderRadius}px;
  border: none;
  background-color: ${(props) =>
    props.color ? alphaColorPallete[props.color].light : alphaColorPallete.inherit.light};
  padding: ${Shapes.spacing[4]}px;
  width: 100%;
  cursor: ${(props) => (props.cursor ? props.cursor : 'default')};
  border: 1px solid ${(props) => alphaColorPallete[props.color].dark};
`

const TextWrapper = styled.div<{ expandable: boolean; expanded: boolean }>`
  display: flex;
  flex: 1;
  gap: ${Shapes.spacing[4]}px;
  min-width: 0;
  align-items: start;
  & > p:first-of-type {
    ${(props) =>
      props.expandable
        ? `
      line-height: 16px !important;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: ${props.expanded ? 'pre-wrap' : 'nowrap'};
    `
        : ``}
  }
  & > svg:first-of-type {
    height: auto;
    width: auto;
    max-width: 14px;
  }
`

const ExpandButton = styled(ButtonWithIcon)`
  background-color: transparent;
  :hover {
    background-color: transparent;
  }
  svg {
    margin-left: ${Shapes.spacing[1]}px;
    margin-right: 0;
  }
`

type AlertHelpProps = {
  color: keyof ColorPallete
  message: React.ReactNode
  icon?: React.ReactNode
  style?: React.CSSProperties
  expandByWrapper?: boolean
  expandable?: boolean
  className?: string
  variant?: keyof TypographyPropsVariantOverrides
}

export const AlertHelp = (props: AlertHelpProps) => {
  const { color, message, icon, style, expandable, expandByWrapper, className, variant } = props
  const iconColor = alphaColorPallete[color].dark
  const [expanded, setExpanded] = useState(false)

  return (
    <Wrapper
      color={color}
      style={style}
      onClick={() => expandByWrapper && setExpanded(!expanded)}
      cursor={expandable && expandByWrapper ? 'pointer' : 'default'}
      className={className}>
      <TextWrapper expandable={!!expandable} expanded={expanded}>
        {icon ? (
          <CircleIcon size={18} color={iconColor} padding={4}>
            {icon}
          </CircleIcon>
        ) : (
          <AlphamapInfoIcon fill={iconColor} />
        )}
        {typeof message === 'string' ? (
          <Typography variant={variant || 'text1m'} color={Colors.textDark}>
            {message}
          </Typography>
        ) : (
          message
        )}
      </TextWrapper>
      {expandable && (
        <ExpandButton color={iconColor} ghost onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Hide' : 'Expand'}
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </ExpandButton>
      )}
    </Wrapper>
  )
}
